import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { faBooks } from '@fortawesome/pro-light-svg-icons';
import { select, Store, } from '@ngrx/store';
import { EditionData } from 'app/citation/models/editions';
import { DiscoverV2BffService } from 'app/elements-v2/service/discover-v2-bff.service';
import { Cover } from 'app/models/cover';
import { I18NService } from 'app/services/i18-n.service';
import { ShareItResource, ShareItShareEntityType } from 'app/share-it/models/share-it';
import { QueryHelper } from 'core/utils/query-helper';
import { combineLatest } from 'rxjs';
import { Subscription } from 'rxjs/internal/Subscription';
import {
  isAuthorizedInKeycloak,
  isStaffAuthorizedInKeycloak,
} from 'app/keycloak/reducers/keycloak.reducer';
import { ResetFacetBubblesAction, UpdateSearchObjectAction } from 'search/actions/facet.actions';
import { SearchResultsReset } from 'search/actions/search.actions';
import { FacetURLFilters } from 'search/facets/models/facet-state';
import { SearchType } from 'search/models/search-type';
import { getLockedFacetsAsQueryParams } from 'search/reducers/root.reducer';
import { CustomerFeatureToggleService } from '../../../../customer-integration/services/customer-feature-toggle.service';
import { DictionariesService } from '../../../../services/dictionaries.service';
import { FeatureToggleService } from '../../../../services/feature-toggle.service';
import { resetScrollAndSelectedResourceId, setSelectedResourceId } from '../../../actions/entity.actions';
import {
  AvailabilityStatus,
  AvailabilityUpdateInfo,
  AvailabilityVendorType,
  Edition,
  EntityTypes,
  FormatGroup,
  GetItRequest,
  HoldRequest,
} from '../../../models/entity';
import { getSelectedRollupTabIndex } from '../../../reducers/entity.reducer';
import { WindowRefService } from '../../../../services/window-ref.service';
import { ShareOptions } from '../../../../models/MetaData';
import { RapidoOffersService } from 'app/services/rapido-offers.service';

@Component({
  selector: 'app-rollup-card-rapido',
  templateUrl: './rollup-card-rapido.component.html',
  styleUrls: ['./rollup-card-rapido.component.scss'],
})
export class RollupCardRapidoComponent implements OnInit, OnDestroy {
  @Input() public formatGroup: FormatGroup;
  @Input() public isListView?: boolean;
  @Output() public getIt$ = new EventEmitter<{ item: GetItRequest }>(true);
  @Output() public placeHold$ = new EventEmitter<{ item: HoldRequest }>(true);
  @ViewChild('searchCard', { static: true }) public searchCard: ElementRef;

  public queryParams = {};
  public subscription = new Subscription();
  public notFoundWords: string[] = [];
  public ariaTitleId: string;
  public entityTypes = EntityTypes;
  public resourceData: ShareItResource;
  public editionsData: EditionData[];
  public coverConfig: Cover;
  public language: string;
  public lockedFacetsQueryParams: FacetURLFilters;
  public originalSearchQueryValue: string;
  public originalSearchType: string;
  public readonly booksIcon = faBooks;
  public selectedTabName: string;
  public selectedTabRecordId: string;
  public author: string;
  public title: string;
  public shareData: ShareOptions;
  public materialType: string;
  public individualResultOnSrp: boolean
  public offers: any[] = [];
  public availabilityUpdates: AvailabilityUpdateInfo[] = [];
  public isPatronAuthorized: boolean = false;
  public isStaffAuthorized: boolean = false;
  public isUserLoggedIn: boolean = false;

  public constructor(
    private readonly store: Store,
    private readonly i18nService: I18NService,
    private readonly router: Router,
    private readonly queryHelper: QueryHelper,
    private readonly route: ActivatedRoute,
    private readonly dictionaryService: DictionariesService,
    private readonly discoverV2BffService: DiscoverV2BffService,
    private readonly featureToggleService: FeatureToggleService,
    private readonly customerFeatureToggleService: CustomerFeatureToggleService,
    private readonly windowRefService: WindowRefService,
    private readonly rapidoOffersService: RapidoOffersService
  ) {}

  public ngOnInit(): void {
    this.language = this.i18nService.getLangFacetTitle(this.formatGroup.language);
    this.coverConfig = {
      coverUrl: this.formatGroup.coverUrl,
    };

    this.subscription.add(
      combineLatest([
        this.store.select(isAuthorizedInKeycloak),
        this.store.select(isStaffAuthorizedInKeycloak),
      ]).subscribe(([isPatronAuth, isStaffAuth]) => {
        this.isPatronAuthorized = isPatronAuth;
        this.isStaffAuthorized = isStaffAuth;
        this.isUserLoggedIn = isPatronAuth || isStaffAuth;
        this.availabilityUpdates =
          this.formatGroup?.materialTabs?.map((tab, index) => ({
            status: {
              [AvailabilityVendorType.GENERAL]:
                AvailabilityStatus.NOT_LOGGED_IN,
            },
            holds: 0,
            copies: 0,
          })) || [];
      })
    );

    this.subscription.add(this.store.pipe(select(getSelectedRollupTabIndex(this.formatGroup.id))).subscribe((selectedTabIndex) => {
      if (this.formatGroup?.materialTabs?.length) {
        this.extractCitationsData(selectedTabIndex);
      }
    }));
    this.subscription.add(this.discoverV2BffService.$page.subscribe((page) => {
      if (page) {
        this.individualResultOnSrp = page.settings?.social_media_display?.individual_result_on_srp;
      }
    }));
    this.subscription.add(this.store.pipe(select(getLockedFacetsAsQueryParams)).subscribe((result) => {
      this.lockedFacetsQueryParams = result;
    }));

    this.subscription.add(this.route.queryParamMap.subscribe((params) => {
      this.originalSearchQueryValue = params.get('query');
      this.originalSearchType = params.get('searchType');
    }));

    this.notFoundWords = this.formatGroup && this.formatGroup.missingWords|| [];

    this.queryParams = {
      id: this.formatGroup.id,
      entityType: this.formatGroup.entityType,

    };
    this.ariaTitleId = `ariaTitleRollupCardId-${this.formatGroup.id}`;
    this.title = this.formatGroup?.title;
    this.author = this.formatGroup?.primaryAgent?.label;
    this.materialType = this.dictionaryService.getFormatDescriptionByCode(this.formatGroup?.materialTabs?.[0]?.materialTypes?.[0]);

  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public onSaveFocus(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.type === 'click') {
      this.store.dispatch(setSelectedResourceId({resourceId: this.formatGroup.id}));
    }
  }

  public onSeriesTypeSearch(event: KeyboardEvent, seriesTitle: string, seriesTitleSearch: string) {
    const searchText = `"${(seriesTitleSearch || seriesTitle).replace(/"/g, '')}"`;
    const searchType = SearchType.SERIES;
    if ((event.key === 'Enter' || event.type === 'click') &&
      (this.originalSearchQueryValue !== searchText || searchType !== this.originalSearchType)) {
      this.store.dispatch(resetScrollAndSelectedResourceId());
      this.store.dispatch(new SearchResultsReset());
      this.store.dispatch(new UpdateSearchObjectAction({searchParams: {type: {searchText: searchText }}, searchType}));
      this.store.dispatch(new ResetFacetBubblesAction({}));
      const queryParams = this.queryHelper.prepareQueryParams(searchText, this.lockedFacetsQueryParams, searchType);
      this.router.navigate(['/search'], queryParams);
    }
  }

  public extractCitationsData(selectedTabIndex: number) {
    this.editionsData = this.formatGroup.materialTabs?.[selectedTabIndex]?.editions?.map((edition: Edition) => ({
      instanceId: edition.id,
      editionStatement: [edition.edition],
      publicationDate: edition.publicationDate,
    } as EditionData));
    this.selectedTabName = this.formatGroup.materialTabs?.[selectedTabIndex]?.name;
    this.selectedTabRecordId = this.formatGroup.materialTabs?.[selectedTabIndex]?.editions?.[0]?.recordId;
    this.formatGroup.selectedTabRecordId = this.selectedTabRecordId;

    this.resourceData = {
      type: ShareItShareEntityType.RESOURCE,
      attributes: {
        id: this.formatGroup.id,
        url: `/search/card?recordId=${this.selectedTabRecordId}`,
        title: this.formatGroup.title,
        author: this.formatGroup.primaryAgent?.label || '',
        publicationDate: this.formatGroup.publicationDate || '',
        resourceType: this.formatGroup.entityType,
      }
    };
  }
}
